import WhatsNewBell from "@/shared/components/WhatsNew/WhatsNewBell";
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import SearchBar from "@/shared/components/ui/SearchBar/SearchBar";
import CustomTable from "@/shared/components/ui/Button/CustomTable";
import CommonDropdownMenu from "@/shared/components/ui/DropDown/DropdownMenu";
import { MapPin, Trash, Trash2, Users } from "lucide-react";
import { useMainStore } from "@/stores";
import { useNavigate } from "react-router-dom";
import { AccountsColumns } from "@/shared/utils/utils";
import coinStack from "@/assets/svg/coin-stack.svg";
import CommonSelect from "@/shared/components/ui/Select/CommonSelect";
import {
  useBulkDeleteAccounts,
  useDeleteAccount,
  useFetchAccounts,
  useUpdateAccountStatus,
} from "@/features/accounts/hooks/useAccounts";
import NoDataCommonCard from "@/shared/components/ui/NoDataView/NoDataCommon";
import NoDataTable from "@/shared/components/ui/NoDataView/NoDataTable";
import NoDataFound from "@/shared/components/ui/NoDataView/NoDataFound";
import Loader from "@/shared/components/ui/Loaders/Loader";
import PaginationInfo from "@/shared/components/ui/Pagination/PaginationInfo";
import CommonAvatar from "@/shared/components/ui/Avatar/CommonAvatar";
import DeleteConfirmationModal from "@/shared/components/ui/Modal/DeleteConfirmationModal";
import noAccountsImage from "@/assets/images/onboarding1.gif";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import AddAccountModal from "./AddAccountModal";
import {
  getCleanName,
  getDisplayLocation,
} from "@/shared/utils/accountConfig";
import {
  formatPeopleCountShort,
  formatRevenueShort,
} from "@/shared/utils/helperFunctions";
import { useDynamicRowsPerPage } from "@/shared/hooks/useDynamicRowsPerPage";
import TrialRemainingCount from "@/shared/components/ui/TrialRemainingCount";

export default function Accounts() {
  const navigate = useNavigate();
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deletingAccountData, setDeletingAccountData] = useState(null);
  const { user } = useMainStore();
  const { data: accountsData, isLoading: isLoadingAccounts } = useFetchAccounts(
    {
      userId: user?.id,
      enabled: !!user?.id,
    },
  );
  const { mutate: deleteAccount, isPending: isDeletingAccount } =
    useDeleteAccount({
      enabled: !!user?.id && !!deletingAccountData,
      action: () => {
        setShowDeleteConfirmModal(false);
        setDeletingAccountData(null);
        setDeleteConfirmKind(null);
      },
    });
  const { mutate: bulkDeleteAccounts, isPending: isBulkDeleting } =
    useBulkDeleteAccounts({
      action: () => {
        setShowDeleteConfirmModal(false);
        setSelectedAccounts([]);
        setDeleteConfirmKind(null);
      },
    });
  const { mutate: updateAccountStatus, isPending: isUpdatingStatus } =
    useUpdateAccountStatus();
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [filterValue, setFilterValue] = useState("");
  const rowsPerPage = useDynamicRowsPerPage(15);
  const [page, setPage] = useState(1);
  const [deleteConfirmKind, setDeleteConfirmKind] = useState(null);
  const sidebarExpanded = useMainStore((state) => state.sidebarExpanded);

  // delete account
  const handleDelete = useCallback((data) => {
    setShowDeleteConfirmModal(true);
    setDeletingAccountData(data);
    setDeleteConfirmKind("single");
  }, []);

  const handleDeleteAccount = useCallback(() => {
    deleteAccount({ user_id: user?.id, company_name: deletingAccountData });
  }, [deleteAccount, user?.id, deletingAccountData]);

  const actions = useMemo(() => {
    return [
      {
        id: "delete",
        name: "Delete",
        isDanger: true,
        onClick: handleDelete,
        icon: <Trash size={16} className="text-red-500" />,
      },
    ];
  }, [handleDelete]);

  // data
  const displayData = useMemo(
    () =>
      (accountsData || []).map((item) => ({
        ...item,
        id: item?.id ?? item?.company_name,
      })),
    [accountsData],
  );

  const renderCell = useCallback(
    (company, columnKey) => {
      const cellValue = company[columnKey];
      switch (columnKey) {
        case "company_name":
          return (
            <div className="flex items-center gap-1.5">
              <CommonAvatar
                imageUrl={company?.logo}
                name={company?.company_name}
              />

              <p className="text-[0.763rem] text-gray-800 dark:text-gray-100 cursor-pointer">
                {getCleanName(company?.company_name)}
              </p>
            </div>
          );
        case "companySize":
        case "company_size":
          const hasKnownHeadcountRegion =
            !company.headcount ||
            getDisplayLocation(company.headcount.region) !== "Not Identified";
          const sizeVal = hasKnownHeadcountRegion
            ? company.headcount?.value || company.company_size || cellValue
            : null;
          return (
            <div className="flex items-center gap-x-2.5">
              <Users size={16} color="#717680" strokeWidth={2.2} />{" "}
              <p className="text-[0.763rem] text-gray-500 dark:text-gray-300 font-normal">
                {formatPeopleCountShort(sizeVal)}
              </p>
            </div>
          );
        case "revenue":
          const displayRevenue = formatRevenueShort(cellValue, { fallback: "" });
          return (
            <div className="flex items-center gap-x-2.5">
              <img src={coinStack} alt="coin-stack" className="w-4 h-4" />
              <div className="text-[0.763rem] font-normal text-gray-500 dark:text-gray-300">
                {displayRevenue || <span className="text-gray-400">Not researched</span>}
              </div>
            </div>
          );
        case "location":
          return (
            <div className="flex items-center gap-x-2.5">
              <MapPin size={16} color="#717680" strokeWidth={2.2} />
              <p className="text-[0.763rem] text-gray-500 dark:text-gray-300 w-[130px] text-wrap">
                {getDisplayLocation(cellValue)}
              </p>
            </div>
          );
        case "status":
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <CommonSelect
                value={company.status || "Inprocess"}
                disabled={isUpdatingStatus}
                width="w-[130px]"
                options={[
                  { label: "Won", value: "Win" },
                  { label: "Lost", value: "Lost" },
                  { label: "Inprocess", value: "Inprocess" },
                ]}
                onChange={(value) => {
                  updateAccountStatus({
                    userId: user?.id,
                    companyName: company.company_name,
                    status: value,
                  });
                }}
                ariaLabel={`Status for ${company.company_name}`}
                classNames={{
                  base: "min-h-0",
                  trigger: "h-7 min-h-7 rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800 px-2 py-1 text-[0.763rem] text-gray-700 dark:text-gray-200 outline-none focus:border-secondary",
                  value: "text-[0.763rem] text-gray-700 dark:text-gray-200",
                }}
              />
            </div>
          );
        case "actions":
          return (
            <div className="flex items-center justify-center gap-2">
              <CommonDropdownMenu
                actions={actions}
                itemId={company?.company_name}
              />
            </div>
          );
        default:
          return (
            <p className="text-[0.763rem] font-normal text-gray-500 dark:text-gray-300">
              {cellValue}
            </p>
          );
      }
    },
    [actions, isUpdatingStatus, updateAccountStatus, user?.id],
  );



  const handleRowClick = (row) => {
    navigate(`/accounts/${encodeURIComponent(row?.company_name)}/account360`);
  };

  const onSearchChange = useCallback((value) => {
    setFilterValue(value);
    setPage(1);
  }, []);

  // Filter data based on search
  const filteredData = React.useMemo(() => {
    if (!filterValue) return displayData;

    return displayData?.filter((item) =>
      (item.name ?? item.company_name ?? "")
        .toLowerCase()
        .includes(filterValue.toLowerCase()),
    );
  }, [filterValue, displayData]);

  const totalFiltered = filteredData?.length ?? 0;
  const totalPages =
    rowsPerPage > 0 ? Math.max(1, Math.ceil(totalFiltered / rowsPerPage)) : 1;

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [rowsPerPage]);

  const handleBulkDelete = useCallback(() => {
    setDeleteConfirmKind("bulk");
    setShowDeleteConfirmModal(true);
  }, []);

  const handleConfirmBulkDelete = useCallback(() => {
    const companyNames = selectedAccounts.map((account) => account.name).filter(Boolean);
    if (companyNames.length > 0 && user?.id) {
      bulkDeleteAccounts({
        user_id: user.id,
        company_names: companyNames,
      });
    }
  }, [bulkDeleteAccounts, selectedAccounts, user?.id]);

  // selection
  const handleSelectionChange = useCallback((accounts) => {
    const minimal = accounts.map((a) => ({
      id: a.id,
      name: a.name || a.company_name || "",
    }));
    setSelectedAccounts(minimal);
  }, []);

  // clear selection
  const clearSelection = useCallback(() => {
    setSelectedAccounts([]);
  }, []);

  return (
    <div className="flex flex-col px-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="text-[1.375rem] text-gray-800 dark:text-gray-100 font-bold">Accounts</h1>
          <TrialRemainingCount resource="accounts" />
        </div>
        <WhatsNewBell compact className="hidden md:flex" />
      </div>
      <div className=" w-full mt-4">
        {isLoadingAccounts ? (
          <div className="flex items-center justify-center  h-[calc(100dvh-121px)]">
            <Loader text="Loading accounts..." />
          </div>
        ) : (
          <>
            <div className="flex flex-col h-full w-full">
              {accountsData?.length > 0 ? (
                <>
                  <div className="flex w-full justify-between items-center">
                    <div className="flex gap-1.5 w-full items-center">
                      <SearchBar
                        value={filterValue}
                        setFilterValue={setFilterValue}
                        onChange={onSearchChange}
                        placeholder="Search"
                        className={`w-full  ${sidebarExpanded ? " max-w-[220px]" : "max-w-[280px]"} duration-300 transition-all`}
                      />
                      <PaginationInfo
                        totalCount={totalFiltered}
                        currentPage={page}
                        rowsPerPage={rowsPerPage}
                        itemName="account"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedAccounts.length > 1 && (
                        <>
                          <SecondaryButton
                            title={
                              selectedAccounts.length > 1
                                ? `Delete (${selectedAccounts.length})`
                                : "Delete"
                            }
                            icon={<Trash2 size={16} className="text-error-500" />}
                            onClick={handleBulkDelete}
                            disabled={isBulkDeleting}
                            isLoading={isBulkDeleting}
                            className="shadow-none border-error-500 text-error-500"
                          />
                          <SecondaryButton
                            title="Clear Selection"
                            onClick={clearSelection}
                            className="shadow-none"
                          />
                        </>
                      )}
                      <PrimaryButton onClick={() => setShowAddAccountModal(true)}>
                        Add Account
                      </PrimaryButton>
                    </div>
                  </div>

                  {filteredData?.length > 0 ? (
                    <div className="mt-4">
                      <CustomTable
                        renderCell={renderCell}
                        columns={AccountsColumns}
                        data={filteredData ?? []}
                        totalCount={totalFiltered}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={setPage}
                        enableSelection={true}
                        selectedItems={selectedAccounts}
                        setSelectedItems={setSelectedAccounts}
                        onRowClick={handleRowClick}
                        onClearSelection={clearSelection}
                        onSelectionChange={handleSelectionChange}
                        height="calc(100dvh - 136px)"
                        pl="pl-2"
                        pr="pr-2"
                        pinFirstColumn
                        showCellBorders
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center min-h-[350px] w-full">
                      <NoDataFound
                        title="No Accounts Found"
                        description={`No accounts found matching "${filterValue}"`}
                        searchValue={filterValue}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full relative">
                  <NoDataTable />
                  <NoDataCommonCard
                    title="No Accounts Yet"
                    description="Add a company link to generate your first account"
                    buttonText="Add Account"
                    onClick={() => setShowAddAccountModal(true)}
                    image={noAccountsImage}
                  />
                </div>
              )}
            </div>
            {showDeleteConfirmModal && (
              <DeleteConfirmationModal
                title={
                  deleteConfirmKind === "bulk"
                    ? `Delete ${selectedAccounts.length} Accounts?`
                    : `Delete ${deletingAccountData} Account?`
                }
                description={
                  deleteConfirmKind === "bulk"
                    ? `Are you sure you want to delete ${selectedAccounts.length} accounts? This action can't be undone.`
                    : `Are you sure you want to delete ${deletingAccountData} account? This action can't be undone.`
                }
                showConfirmDelete={showDeleteConfirmModal}
                onClose={() => {
                  setShowDeleteConfirmModal(false);
                  setDeleteConfirmKind(null);
                }}
                onDelete={
                  deleteConfirmKind === "bulk"
                    ? handleConfirmBulkDelete
                    : handleDeleteAccount
                }
                isDeleting={
                  deleteConfirmKind === "bulk"
                    ? isBulkDeleting
                    : isDeletingAccount
                }
                deleteButtonText={
                  deleteConfirmKind === "bulk"
                    ? "Yes, delete accounts"
                    : "Yes, delete account"
                }
                cancelButtonText="I changed my mind"
              />
            )}
            <AddAccountModal
              isOpen={showAddAccountModal}
              onClose={() => setShowAddAccountModal(false)}
            />
          </>
        )}
      </div>
    </div>
  );
}
